// 图像处理工具函数：纯 JS + Canvas/ImageData
// 用于拼豆图纸生成管线

import { findNearestColor, getBrandOklabColors, oklabDistance, hexToRgb } from './color'

// 直接从原图采样时，对超大图片做分辨率上限，平衡精度与内存/性能
// 1024 已足够保留五官等细部，同时避免 Canny + 采样阻塞主线程
const MAX_SOURCE_SIZE = 1024

const clampByte = (v) => Math.max(0, Math.min(255, Math.round(v)))

function getLuma({ r, g, b }) {
  return 0.299 * r + 0.587 * g + 0.114 * b
}

function getSaturation({ r, g, b }) {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  return max === 0 ? 0 : (max - min) / max
}

// 边缘检测结果缓存（key = HTMLImageElement）

/**
 * 把图片缩放到 N×M 的画布上
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @param {number} N 目标宽度（列数）
 * @param {number} M 目标高度（行数）
 * @param {'stretch'|'fit'|'pad'} mode
 * @returns {HTMLCanvasElement}
 */
export function resizeToGrid(image, N, M, mode = 'stretch') {
  const canvas = document.createElement('canvas')
  canvas.width = N
  canvas.height = M
  const ctx = canvas.getContext('2d')

  ctx.imageSmoothingEnabled = true
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, N, M)

  const srcW = image.naturalWidth || image.width
  const srcH = image.naturalHeight || image.height
  const imgRatio = srcW / srcH
  const boardRatio = N / M

  let sx = 0
  let sy = 0
  let sw = srcW
  let sh = srcH
  let dx = 0
  let dy = 0
  let dw = N
  let dh = M

  if (mode === 'fit') {
    // 等比例缩放后居中裁剪，填满 N×M
    if (imgRatio > boardRatio) {
      sw = sh * boardRatio
      sx = (srcW - sw) / 2
    } else {
      sh = sw / boardRatio
      sy = (srcH - sh) / 2
    }
  } else if (mode === 'pad') {
    // 等比例缩放后居中粘贴到白色画布上
    if (imgRatio > boardRatio) {
      dh = dw / imgRatio
      dy = (M - dh) / 2
    } else {
      dw = dh * imgRatio
      dx = (N - dw) / 2
    }
  }
  // stretch：默认行为，直接拉伸填满

  ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh)
  return canvas
}

/**
 * 计算单元格内颜色
 * @param {ImageData} imageData
 * @param {number} startX
 * @param {number} startY
 * @param {number} width
 * @param {number} height
 * @param {'average'|'dominant'|'detail'} mode
 * @returns {{r:number,g:number,b:number}|null}
 */
export function getCellColor(imageData, startX, startY, width, height, mode = 'average') {
  const { width: imgW, height: imgH, data } = imageData
  const x0 = Math.max(0, Math.floor(startX))
  const y0 = Math.max(0, Math.floor(startY))
  const x1 = Math.min(imgW, Math.ceil(startX + width))
  const y1 = Math.min(imgH, Math.ceil(startY + height))
  if (x1 <= x0 || y1 <= y0) return null

  let r = 0
  let g = 0
  let b = 0
  let count = 0
  const freq = mode === 'dominant' ? new Map() : null
  const samples = mode === 'detail' ? [] : null

  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y * imgW + x) * 4
      const a = data[i + 3]
      if (a === 0) continue
      const pr = data[i]
      const pg = data[i + 1]
      const pb = data[i + 2]
      r += pr
      g += pg
      b += pb
      count++
      if (samples) samples.push({ r: pr, g: pg, b: pb, x: x + 0.5, y: y + 0.5 })
      if (freq) {
        const key = `${pr},${pg},${pb}`
        freq.set(key, (freq.get(key) || 0) + 1)
      }
    }
  }

  if (count === 0) return null

  if (mode === 'dominant') {
    let bestKey = null
    let bestCount = -1
    for (const [key, sampleCount] of freq) {
      if (sampleCount > bestCount) {
        bestCount = sampleCount
        bestKey = key
      }
    }
    const [rr, gg, bb] = bestKey.split(',').map(Number)
    return { r: rr, g: gg, b: bb }
  }

  if (mode === 'detail') {
    const average = { r: r / count, g: g / count, b: b / count }
    const centerX = (x0 + x1) / 2
    const centerY = (y0 + y1) / 2
    const halfW = Math.max(1, (x1 - x0) / 2)
    const halfH = Math.max(1, (y1 - y0) / 2)
    let wr = 0
    let wg = 0
    let wb = 0
    let weightSum = 0
    let cr = 0
    let cg = 0
    let cb = 0
    let centerWeightSum = 0

    for (const sample of samples) {
      const dr = sample.r - average.r
      const dg = sample.g - average.g
      const db = sample.b - average.b
      const colorDistance = Math.sqrt(dr * dr + dg * dg + db * db)
      const nx = (sample.x - centerX) / halfW
      const ny = (sample.y - centerY) / halfH
      const radial = nx * nx + ny * ny

      // Center weighting keeps aligned eyes and line work; robust weighting rejects JPEG speckles.
      const spatialWeight = 1 + 1.4 * Math.exp(-radial * 2.2)
      const robustWeight = 1 / (1 + (colorDistance / 72) ** 2)
      const weight = spatialWeight * robustWeight
      wr += sample.r * weight
      wg += sample.g * weight
      wb += sample.b * weight
      weightSum += weight

      const centerWeight = Math.exp(-radial * 5)
      cr += sample.r * centerWeight
      cg += sample.g * centerWeight
      cb += sample.b * centerWeight
      centerWeightSum += centerWeight
    }

    const robust = { r: wr / weightSum, g: wg / weightSum, b: wb / weightSum }
    const center = {
      r: cr / centerWeightSum,
      g: cg / centerWeightSum,
      b: cb / centerWeightSum,
    }
    const centerContrast = Math.abs(getLuma(center) - getLuma(robust))
    const detailBlend = Math.min(0.48, Math.max(0, (centerContrast - 16) / 100))

    return {
      r: clampByte(robust.r * (1 - detailBlend) + center.r * detailBlend),
      g: clampByte(robust.g * (1 - detailBlend) + center.g * detailBlend),
      b: clampByte(robust.b * (1 - detailBlend) + center.b * detailBlend),
    }
  }

  return {
    r: clampByte(r / count),
    g: clampByte(g / count),
    b: clampByte(b / count),
  }
}

// --- 边缘检测（Canny 简化版，用于自动描边）---

function rgbToGray(imageData) {
  const { data, width, height } = imageData
  const gray = new Uint8Array(width * height)
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])
  }
  return gray
}

function gaussianKernel1D(radius, sigma) {
  const size = radius * 2 + 1
  const kernel = new Float32Array(size)
  const s2 = sigma * sigma
  let sum = 0
  for (let i = 0; i < size; i++) {
    const x = i - radius
    const v = Math.exp(-(x * x) / (2 * s2)) / (Math.sqrt(2 * Math.PI) * sigma)
    kernel[i] = v
    sum += v
  }
  for (let i = 0; i < size; i++) kernel[i] /= sum
  return kernel
}

function gaussianBlur(gray, width, height, sigma = 1.4) {
  const radius = Math.max(1, Math.floor(sigma * 3))
  const kernel = gaussianKernel1D(radius, sigma)
  const temp = new Float32Array(width * height)
  const out = new Float32Array(width * height)

  // horizontal pass
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0
      for (let k = 0; k < kernel.length; k++) {
        const sx = Math.max(0, Math.min(width - 1, x + k - radius))
        sum += gray[y * width + sx] * kernel[k]
      }
      temp[y * width + x] = sum
    }
  }
  // vertical pass
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0
      for (let k = 0; k < kernel.length; k++) {
        const sy = Math.max(0, Math.min(height - 1, y + k - radius))
        sum += temp[sy * width + x] * kernel[k]
      }
      out[y * width + x] = sum
    }
  }
  return out
}

function sobelGradient(blurred, width, height) {
  const mag = new Float32Array(width * height)
  const dir = new Float32Array(width * height)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x
      const p00 = blurred[i - width - 1]
      const p01 = blurred[i - width]
      const p02 = blurred[i - width + 1]
      const p10 = blurred[i - 1]
      const p12 = blurred[i + 1]
      const p20 = blurred[i + width - 1]
      const p21 = blurred[i + width]
      const p22 = blurred[i + width + 1]
      const gx = -p00 - 2 * p10 - p20 + p02 + 2 * p12 + p22
      const gy = -p00 - 2 * p01 - p02 + p20 + 2 * p21 + p22
      mag[i] = Math.sqrt(gx * gx + gy * gy)
      dir[i] = Math.atan2(gy, gx)
    }
  }
  return { mag, dir }
}

function nonMaxSuppression(mag, dir, width, height) {
  const out = new Float32Array(width * height)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x
      const angle = dir[i]
      const deg = ((angle * 180) / Math.PI + 180) % 180
      let neighborA = 0
      let neighborB = 0
      if ((deg >= 0 && deg < 22.5) || (deg >= 157.5 && deg < 180)) {
        neighborA = mag[i - 1]
        neighborB = mag[i + 1]
      } else if (deg >= 22.5 && deg < 67.5) {
        neighborA = mag[i - width + 1]
        neighborB = mag[i + width - 1]
      } else if (deg >= 67.5 && deg < 112.5) {
        neighborA = mag[i - width]
        neighborB = mag[i + width]
      } else {
        neighborA = mag[i - width - 1]
        neighborB = mag[i + width + 1]
      }
      out[i] = mag[i] >= neighborA && mag[i] >= neighborB ? mag[i] : 0
    }
  }
  return out
}

function hysteresisThreshold(suppressed, width, height, lowRatio = 0.05, highRatio = 0.15) {
  let maxVal = 0
  for (let i = 0; i < suppressed.length; i++) {
    if (suppressed[i] > maxVal) maxVal = suppressed[i]
  }
  const high = maxVal * highRatio
  const low = maxVal * lowRatio
  const edges = new Uint8Array(width * height)
  const stack = []

  // 强边缘
  for (let i = 0; i < suppressed.length; i++) {
    if (suppressed[i] >= high) {
      edges[i] = 255
      stack.push(i)
    }
  }

  // 从强边缘向弱边缘生长
  while (stack.length) {
    const i = stack.pop()
    const x = i % width
    const y = Math.floor(i / width)
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue
        const ni = ny * width + nx
        if (edges[ni] === 0 && suppressed[ni] >= low) {
          edges[ni] = 255
          stack.push(ni)
        }
      }
    }
  }
  return edges
}

export function cannyEdgeMask(imageData, lowRatio = 0.05, highRatio = 0.15) {
  const { width, height } = imageData
  const gray = rgbToGray(imageData)
  const blurred = gaussianBlur(gray, width, height, 1.0)
  const { mag, dir } = sobelGradient(blurred, width, height)
  const suppressed = nonMaxSuppression(mag, dir, width, height)
  return hysteresisThreshold(suppressed, width, height, lowRatio, highRatio)
}

/**
 * Floyd-Steinberg 抖动量化
 * @param {{r:number,g:number,b:number}[][]} pixelGrid 二维 RGB 数组
 * @param {any[]} palette 品牌色板
 * @returns {string[][]} 二维 paletteColorId 数组
 */
function rgbDistance(a, b) {
  return Math.hypot(a.r - b.r, a.g - b.g, a.b - b.b)
}

const clamp01 = (value) => Math.max(0, Math.min(1, value))

/**
 * Estimate structural detail before quantization. High values mark contours,
 * facial features and highlights where smoothing/dither diffusion must stop.
 */
export function buildDetailMask(pixelGrid) {
  const rows = pixelGrid.length
  const cols = pixelGrid[0]?.length || 0
  const mask = Array.from({ length: rows }, () => Array(cols).fill(0))
  if (!rows || !cols) return mask

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const center = pixelGrid[y][x]
      const centerLuma = getLuma(center)
      let maxLumaDiff = 0
      let maxColorDiff = 0
      let lumaDiffSum = 0
      let colorDiffSum = 0
      let neighbors = 0

      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue
          const ny = y + dy
          const nx = x + dx
          if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
          const neighbor = pixelGrid[ny][nx]
          const lumaDiff = Math.abs(centerLuma - getLuma(neighbor))
          const colorDiff = rgbDistance(center, neighbor)
          maxLumaDiff = Math.max(maxLumaDiff, lumaDiff)
          maxColorDiff = Math.max(maxColorDiff, colorDiff)
          lumaDiffSum += lumaDiff
          colorDiffSum += colorDiff
          neighbors++
        }
      }

      if (!neighbors) continue
      const lumaSignal = (maxLumaDiff * 0.72 + lumaDiffSum / neighbors * 0.28) / 92
      const colorSignal = (maxColorDiff * 0.72 + colorDiffSum / neighbors * 0.28) / 184
      mask[y][x] = clamp01((Math.max(lumaSignal, colorSignal) - 0.1) / 0.9)
    }
  }

  return mask
}

/**
 * Edge-aware serpentine Floyd-Steinberg quantization.
 * Error is diffused only through smooth regions so gradients remain soft
 * without scattering colors across eyes, outlines or object boundaries.
 */
export function floydSteinbergDither(pixelGrid, palette, strength = 0.56) {
  const rows = pixelGrid.length
  const cols = pixelGrid[0]?.length || 0
  if (!rows || !cols) return []

  const detailMask = buildDetailMask(pixelGrid)
  const errors = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({ r: 0, g: 0, b: 0 }))
  )
  const result = Array.from({ length: rows }, () => Array(cols).fill(null))
  const amount = clamp01(strength)

  for (let y = 0; y < rows; y++) {
    const leftToRight = y % 2 === 0
    const start = leftToRight ? 0 : cols - 1
    const end = leftToRight ? cols : -1
    const step = leftToRight ? 1 : -1

    for (let x = start; x !== end; x += step) {
      const smoothness = (1 - detailMask[y][x]) ** 2
      const source = pixelGrid[y][x]
      const accumulated = errors[y][x]
      const current = {
        r: clampByte(source.r + accumulated.r * smoothness),
        g: clampByte(source.g + accumulated.g * smoothness),
        b: clampByte(source.b + accumulated.b * smoothness),
      }
      const matched = findNearestColor(current.r, current.g, current.b, palette)
      result[y][x] = matched.id

      const matchedRgb = hexToRgb(matched.hex)
      const error = {
        r: (current.r - matchedRgb.r) * amount,
        g: (current.g - matchedRgb.g) * amount,
        b: (current.b - matchedRgb.b) * amount,
      }

      const distribute = (dx, dy, weight) => {
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) return
        const targetSmoothness = (1 - detailMask[ny][nx]) ** 2
        const edgeGate = Math.min(smoothness, targetSmoothness)
        if (edgeGate <= 0.01) return
        errors[ny][nx].r += error.r * weight * edgeGate
        errors[ny][nx].g += error.g * weight * edgeGate
        errors[ny][nx].b += error.b * weight * edgeGate
      }

      const forward = leftToRight ? 1 : -1
      distribute(forward, 0, 7 / 16)
      distribute(-forward, 1, 3 / 16)
      distribute(0, 1, 5 / 16)
      distribute(forward, 1, 1 / 16)
    }
  }

  return result
}

/**
 * 限制调色板颜色数量。
 * 先保留画面主色，再为高对比小特征预留少量颜色，避免眼睛、高光、线条被频次截断。
 * @param {{r:number,g:number,b:number}[]|{r:number,g:number,b:number}[][]} pixelRgbs
 * @param {any[]} palette
 * @param {number} maxColors
 * @returns {any[]}
 */
export function reducePalette(pixelRgbs, palette, maxColors) {
  if (!maxColors || maxColors <= 0 || maxColors >= palette.length) return palette

  const isGrid = Array.isArray(pixelRgbs[0])
  const flat = isGrid ? pixelRgbs.flat() : pixelRgbs
  if (!flat.length) return palette

  const nearestCache = new Map()
  const nearestId = (c) => {
    const key = `${clampByte(c.r)},${clampByte(c.g)},${clampByte(c.b)}`
    if (nearestCache.has(key)) return nearestCache.get(key)
    const matched = findNearestColor(clampByte(c.r), clampByte(c.g), clampByte(c.b), palette)
    nearestCache.set(key, matched.id)
    return matched.id
  }

  const stats = new Map()
  const ensure = (id) => {
    if (!stats.has(id)) {
      stats.set(id, { count: 0, feature: 0, saturation: 0 })
    }
    return stats.get(id)
  }

  if (!isGrid) {
    let samples = flat
    if (samples.length > 5000) {
      const step = samples.length / 5000
      const picked = []
      for (let i = 0; i < 5000; i++) picked.push(samples[Math.floor(i * step)])
      samples = picked
    }

    for (const c of samples) {
      const stat = ensure(nearestId(c))
      stat.count += 1
      stat.saturation += getSaturation(c)
    }

    const selected = Array.from(stats.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, maxColors)
      .map(([id]) => id)
    const selectedSet = new Set(selected)
    const subPalette = palette.filter((c) => selectedSet.has(c.id))
    return subPalette.length ? subPalette : palette
  }

  const grid = pixelRgbs
  const rows = grid.length
  const cols = grid[0]?.length || 0

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const c = grid[y][x]
      const id = nearestId(c)

      let neighborLuma = 0
      let neighborCount = 0
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue
          const ny = y + dy
          const nx = x + dx
          if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
          neighborLuma += getLuma(grid[ny][nx])
          neighborCount++
        }
      }

      const localContrast = neighborCount
        ? Math.abs(getLuma(c) - neighborLuma / neighborCount) / 255
        : 0
      const saturation = getSaturation(c)
      const stat = ensure(id)
      stat.count += 1
      stat.feature += localContrast * (1 + saturation)
      stat.saturation += saturation
    }
  }

  const maxSlots = Math.min(maxColors, palette.length)
  const minFeatureCount = Math.min(4, Math.max(2, Math.round(flat.length * 0.0002)))
  const featureReserve = maxSlots >= 12
    ? Math.min(6, Math.max(2, Math.round(maxSlots * 0.18)))
    : maxSlots >= 4 ? 1 : 0
  const selected = new Set()

  const featureEntries = Array.from(stats.entries())
    .filter(([, stat]) => stat.count >= minFeatureCount && stat.feature >= 0.8)
    .sort((a, b) => {
      const af = a[1].feature / Math.sqrt(a[1].count)
      const bf = b[1].feature / Math.sqrt(b[1].count)
      return bf - af || b[1].feature - a[1].feature
    })

  for (const [id] of featureEntries) {
    if (selected.size >= featureReserve) break
    selected.add(id)
  }

  const countEntries = Array.from(stats.entries())
    .sort((a, b) => b[1].count - a[1].count)

  for (const [id] of countEntries) {
    if (selected.size >= maxSlots) break
    selected.add(id)
  }

  const selectedSet = new Set(selected)
  const subPalette = palette.filter((c) => selectedSet.has(c.id))
  return subPalette.length ? subPalette : palette
}

/**
 * 自动识别主体并裁剪：基于最大连通域去除白边/UI 边框
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @param {number} maxWorkSize 用于连通域分析的最大边长
 * @returns {HTMLCanvasElement} 裁剪后的画布
 */
export function autoCropSubject(image, maxWorkSize = 400) {
  const srcW = image.naturalWidth || image.width
  const srcH = image.naturalHeight || image.height
  const scale = Math.min(1, maxWorkSize / Math.max(srcW, srcH))
  const workW = Math.max(1, Math.round(srcW * scale))
  const workH = Math.max(1, Math.round(srcH * scale))

  const workCanvas = document.createElement('canvas')
  workCanvas.width = workW
  workCanvas.height = workH
  const workCtx = workCanvas.getContext('2d')
  workCtx.drawImage(image, 0, 0, workW, workH)
  const { data } = workCtx.getImageData(0, 0, workW, workH)

  // 背景色：取四角平均
  const sampleSize = 5
  const samples = []
  const addCorner = (cx, cy) => {
    for (let y = cy; y < Math.min(workH, cy + sampleSize); y++) {
      for (let x = cx; x < Math.min(workW, cx + sampleSize); x++) {
        const i = (y * workW + x) * 4
        samples.push([data[i], data[i + 1], data[i + 2]])
      }
    }
  }
  addCorner(0, 0)
  addCorner(Math.max(0, workW - sampleSize), 0)
  addCorner(0, Math.max(0, workH - sampleSize))
  addCorner(Math.max(0, workW - sampleSize), Math.max(0, workH - sampleSize))

  const bg = samples.length
    ? [
        Math.round(samples.reduce((s, c) => s + c[0], 0) / samples.length),
        Math.round(samples.reduce((s, c) => s + c[1], 0) / samples.length),
        Math.round(samples.reduce((s, c) => s + c[2], 0) / samples.length),
      ]
    : [255, 255, 255]

  const dist2 = (i) => {
    const dr = data[i] - bg[0]
    const dg = data[i + 1] - bg[1]
    const db = data[i + 2] - bg[2]
    return dr * dr + dg * dg + db * db
  }

  const threshold = 30 * 30
  const mask = new Uint8Array(workW * workH)
  for (let i = 0; i < mask.length; i++) {
    mask[i] = dist2(i * 4) > threshold ? 1 : 0
  }

  // 连通域标记（4 连通）
  const labels = new Int32Array(workW * workH).fill(-1)
  const sizes = []
  const bboxes = []
  let label = -1

  for (let y = 0; y < workH; y++) {
    for (let x = 0; x < workW; x++) {
      const idx = y * workW + x
      if (!mask[idx] || labels[idx] !== -1) continue
      label++
      sizes.push(0)
      bboxes.push({ minX: x, minY: y, maxX: x, maxY: y })
      const stack = [idx]
      labels[idx] = label
      while (stack.length) {
        const cur = stack.pop()
        sizes[label]++
        const cx = cur % workW
        const cy = Math.floor(cur / workW)
        const b = bboxes[label]
        if (cx < b.minX) b.minX = cx
        if (cx > b.maxX) b.maxX = cx
        if (cy < b.minY) b.minY = cy
        if (cy > b.maxY) b.maxY = cy

        // 右、下、左、上
        const right = cur + 1
        const down = cur + workW
        const left = cur - 1
        const up = cur - workW
        if (cx + 1 < workW && mask[right] && labels[right] === -1) {
          labels[right] = label
          stack.push(right)
        }
        if (cy + 1 < workH && mask[down] && labels[down] === -1) {
          labels[down] = label
          stack.push(down)
        }
        if (cx - 1 >= 0 && mask[left] && labels[left] === -1) {
          labels[left] = label
          stack.push(left)
        }
        if (cy - 1 >= 0 && mask[up] && labels[up] === -1) {
          labels[up] = label
          stack.push(up)
        }
      }
    }
  }

  if (sizes.length === 0) {
    // 没有识别到主体，返回原图
    const full = document.createElement('canvas')
    full.width = srcW
    full.height = srcH
    full.getContext('2d').drawImage(image, 0, 0)
    return full
  }

  // 找最大连通域
  let bestLabel = 0
  for (let i = 1; i < sizes.length; i++) {
    if (sizes[i] > sizes[bestLabel]) bestLabel = i
  }
  const box = bboxes[bestLabel]

  // 映射回原图坐标，并加少量边距
  const padWork = Math.max(4, Math.round(Math.min(workW, workH) * 0.01))
  const minX = Math.max(0, Math.floor(box.minX / scale) - padWork / scale)
  const minY = Math.max(0, Math.floor(box.minY / scale) - padWork / scale)
  const maxX = Math.min(srcW, Math.ceil((box.maxX + 1) / scale) + padWork / scale)
  const maxY = Math.min(srcH, Math.ceil((box.maxY + 1) / scale) + padWork / scale)

  const cropW = Math.max(1, Math.round(maxX - minX))
  const cropH = Math.max(1, Math.round(maxY - minY))
  const out = document.createElement('canvas')
  out.width = cropW
  out.height = cropH
  out.getContext('2d').drawImage(image, minX, minY, cropW, cropH, 0, 0, cropW, cropH)
  return out
}

/**
 * 根据饱和度进一步裁剪：去除白边、坐标数字边距等低饱和度区域
 * @param {HTMLCanvasElement} canvas
 * @returns {HTMLCanvasElement}
 */
export function cropBySaturation(canvas) {
  const w = canvas.width
  const h = canvas.height
  if (w < 20 || h < 20) return canvas

  const ctx = canvas.getContext('2d')
  const { data } = ctx.getImageData(0, 0, w, h)

  // 计算每个像素的 HSV 饱和度
  const sat = new Float32Array(w * h)
  let totalSat = 0
  for (let i = 0; i < w * h; i++) {
    const idx = i * 4
    const r = data[idx] / 255
    const g = data[idx + 1] / 255
    const b = data[idx + 2] / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const s = max === 0 ? 0 : (max - min) / max
    sat[i] = s
    totalSat += s
  }

  const meanSat = totalSat / sat.length
  if (meanSat < 0.01) return canvas // 接近灰度图，不裁剪

  const rowSat = new Float32Array(h)
  const colSat = new Float32Array(w)
  for (let y = 0; y < h; y++) {
    let s = 0
    for (let x = 0; x < w; x++) s += sat[y * w + x]
    rowSat[y] = s / w
  }
  for (let x = 0; x < w; x++) {
    let s = 0
    for (let y = 0; y < h; y++) s += sat[y * w + x]
    colSat[x] = s / h
  }

  // 阈值：饱和度均值的一定倍数，取所有超过阈值的行列的外接框
  const threshold = meanSat * 1.1

  const findRange = (arr) => {
    let first = -1
    let last = -1
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] > threshold) {
        if (first === -1) first = i
        last = i
      }
    }
    if (first === -1) return [0, arr.length - 1]
    return [first, last]
  }

  const [y1, y2] = findRange(rowSat)
  const [x1, x2] = findRange(colSat)
  const cropW = Math.max(1, x2 - x1 + 1)
  const cropH = Math.max(1, y2 - y1 + 1)

  const out = document.createElement('canvas')
  out.width = cropW
  out.height = cropH
  out.getContext('2d').drawImage(canvas, x1, y1, cropW, cropH, 0, 0, cropW, cropH)
  return out
}

/**
 * 合并量化结果中过于相似的颜色
 * @param {string[][]} grid 二维 colorId 数组
 * @param {any[]} palette
 * @param {number} threshold Oklab 距离阈值
 * @returns {string[][]}
 */
export function mergeSimilarColors(grid, palette, threshold) {
  if (!threshold || threshold <= 0) return grid

  const oklabPalette = getBrandOklabColors(palette)

  // 统计出现次数
  const counts = new Map()
  for (const row of grid) {
    for (const id of row) {
      counts.set(id, (counts.get(id) || 0) + 1)
    }
  }

  // 为每个颜色找到频次更高且距离足够近的合并目标
  const mergeTarget = new Map()
  const ids = Array.from(counts.keys())

  for (const id of ids) {
    const self = oklabPalette.find((c) => c.id === id)
    if (!self) continue
    let target = id
    let targetCount = counts.get(id)
    for (const otherId of ids) {
      if (otherId === id) continue
      const other = oklabPalette.find((c) => c.id === otherId)
      if (!other) continue
      if (oklabDistance(self.oklab, other.oklab) < threshold) {
        const otherCount = counts.get(otherId)
        if (otherCount > targetCount) {
          target = otherId
          targetCount = otherCount
        }
      }
    }
    mergeTarget.set(id, target)
  }

  // 解析链式合并
  const resolve = (id) => {
    let cur = id
    const seen = new Set()
    while (mergeTarget.has(cur) && mergeTarget.get(cur) !== cur && !seen.has(cur)) {
      seen.add(cur)
      cur = mergeTarget.get(cur)
    }
    return cur
  }

  return grid.map((row) => row.map((id) => resolve(id)))
}

/**
 * Remove tiny color islands while preserving source-image structure.
 * Standard mode removes isolated near-color speckles; strong mode also
 * removes coherent islands up to three cells when the source has no detail.
 */
export function denoiseGrid(grid, level = 1, sourceGrid = null, palette = null) {
  if (!level || level <= 0) return grid
  const rows = grid.length
  const cols = grid[0]?.length || 0
  if (!rows || !cols) return grid

  const maxComponentSize = level >= 2 ? 3 : 1
  const minimumDominance = level >= 2 ? 0.52 : 0.6
  const detailMask = sourceGrid ? buildDetailMask(sourceGrid) : null
  const paletteMap = palette
    ? new Map(getBrandOklabColors(palette).map((color) => [color.id, color.oklab]))
    : null
  const visited = Array.from({ length: rows }, () => Array(cols).fill(false))
  const output = grid.map((row) => [...row])

  for (let startY = 0; startY < rows; startY++) {
    for (let startX = 0; startX < cols; startX++) {
      if (visited[startY][startX]) continue
      const id = grid[startY][startX]
      const component = []
      const stack = [[startX, startY]]
      visited[startY][startX] = true

      while (stack.length) {
        const [x, y] = stack.pop()
        component.push([x, y])
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue
            const nx = x + dx
            const ny = y + dy
            if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
            if (visited[ny][nx] || grid[ny][nx] !== id) continue
            visited[ny][nx] = true
            stack.push([nx, ny])
          }
        }
      }

      if (component.length > maxComponentSize) continue

      const boundaryCounts = new Map()
      let detailSum = 0
      for (const [x, y] of component) {
        if (detailMask) detailSum += detailMask[y][x]
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue
            const nx = x + dx
            const ny = y + dy
            if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
            const neighborId = grid[ny][nx]
            if (neighborId === id) continue
            boundaryCounts.set(neighborId, (boundaryCounts.get(neighborId) || 0) + 1)
          }
        }
      }

      let targetId = null
      let targetCount = 0
      let boundaryTotal = 0
      for (const [neighborId, count] of boundaryCounts) {
        boundaryTotal += count
        if (count > targetCount) {
          targetId = neighborId
          targetCount = count
        }
      }
      if (!targetId || !boundaryTotal || targetCount / boundaryTotal < minimumDominance) continue

      const meanDetail = detailMask ? detailSum / component.length : 0
      let paletteDistance = 0
      if (paletteMap?.has(id) && paletteMap.has(targetId)) {
        paletteDistance = oklabDistance(paletteMap.get(id), paletteMap.get(targetId))
      }

      const protectedDetail = level >= 2
        ? meanDetail >= 0.68 && paletteDistance >= 0.08
        : meanDetail >= 0.42 && paletteDistance >= 0.055
      if (protectedDetail) continue

      for (const [x, y] of component) output[y][x] = targetId
    }
  }

  return output
}

/**
 * 轻度饱和度增强（让图纸色彩更鲜艳），沿亮度轴拉伸
 */
function boostVibrance({ r, g, b }, factor) {
  const l = 0.299 * r + 0.587 * g + 0.114 * b
  const cl = (v) => Math.max(0, Math.min(255, Math.round(l + (v - l) * factor)))
  return { r: cl(r), g: cl(g), b: cl(b) }
}

/**
 * 主体外轮廓描边：
 * - 只对“主体 vs 背景”的最外轮廓勾勒，五官/衣褶等内部细节一律不动
 * - 背景 = 从图纸四边洪水填充的主导边缘色（白底或纯色底都适用）
 * - 轮廓格本身已是明显深色（如原图自带线稿）时跳过，不重复描边
 * @param {string[][]} grid 颜色 id 网格（原地修改）
 * @param {Object} brand 品牌对象
 */
function applyOutline(grid, brand) {
  const h = grid.length
  const w = grid[0]?.length || 0
  if (!h || !w) return

  // 描边色：优先纯黑，否则取色库最深色
  const outlineColor =
    brand.colors.find((c) => c.hex?.toLowerCase() === '#000000') ||
    brand.colors.reduce((darkest, c) => {
      const rgb = hexToRgb(c.hex)
      const luma = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b
      const dRgb = hexToRgb(darkest.hex)
      const dLuma = 0.299 * dRgb.r + 0.587 * dRgb.g + 0.114 * dRgb.b
      return luma < dLuma ? c : darkest
    }, brand.colors[0])
  if (!outlineColor) return

  // 1. 背景色 = 四条边上出现次数最多的颜色
  const borderCount = new Map()
  const addBorder = (x, y) => borderCount.set(grid[y][x], (borderCount.get(grid[y][x]) || 0) + 1)
  for (let x = 0; x < w; x++) { addBorder(x, 0); addBorder(x, h - 1) }
  for (let y = 1; y < h - 1; y++) { addBorder(0, y); addBorder(w - 1, y) }
  let bgId = null
  let bgMax = 0
  for (const [id, count] of borderCount) {
    if (count > bgMax) { bgMax = count; bgId = id }
  }
  // 边缘主导色占比过低 → 无明确背景，不做外轮廓描边
  const borderLen = 2 * w + 2 * h - 4
  if (!bgId || bgMax < borderLen * 0.4) return

  // 2. 洪水填充背景区域（仅沿背景色连通域）
  const isBg = Array.from({ length: h }, () => Array(w).fill(false))
  const stack = []
  const pushIfBg = (x, y) => {
    if (x < 0 || x >= w || y < 0 || y >= h) return
    if (isBg[y][x] || grid[y][x] !== bgId) return
    isBg[y][x] = true
    stack.push([x, y])
  }
  for (let x = 0; x < w; x++) { pushIfBg(x, 0); pushIfBg(x, h - 1) }
  for (let y = 1; y < h - 1; y++) { pushIfBg(0, y); pushIfBg(w - 1, y) }
  let bgArea = 0
  while (stack.length) {
    const [cx, cy] = stack.pop()
    bgArea++
    pushIfBg(cx + 1, cy)
    pushIfBg(cx - 1, cy)
    pushIfBg(cx, cy + 1)
    pushIfBg(cx, cy - 1)
  }
  // 背景面积不足画布的 1/4 → 主体接近满幅（如棋盘格/风景照），不做外轮廓描边
  if (bgArea < w * h * 0.25) return

  // 3. 深色判定表（识别“已有深色轮廓”）：用最大通道值而非亮度，
  //    避免把纯红/纯蓝等饱和色误判为深色轮廓（纯蓝 luma 仅 29 但显然不是线稿色）
  const darkMap = new Map(brand.colors.map((c) => {
    const rgb = hexToRgb(c.hex)
    return [c.id, Math.max(rgb.r, rgb.g, rgb.b) <= 100]
  }))

  // 4. 外轮廓 = 非背景且与背景 4 邻接的格子；已是深色的保持原色（天然轮廓），否则描黑
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (isBg[y][x]) continue
      if (grid[y][x] === outlineColor.id) continue
      const touchesBg =
        (x > 0 && isBg[y][x - 1]) ||
        (x < w - 1 && isBg[y][x + 1]) ||
        (y > 0 && isBg[y - 1][x]) ||
        (y < h - 1 && isBg[y + 1][x])
      if (!touchesBg) continue
      if (darkMap.get(grid[y][x])) continue
      grid[y][x] = outlineColor.id
    }
  }
}

/**
 * 清除从画布边缘连通的近白背景，同时保护主体里的白色衣物/道具。
 * 返回新网格，空白格使用 null 表示。
 */
export function removeLightEdgeBackground(grid, brand, sourceGrid = null) {
  const rows = grid.length
  const cols = grid[0]?.length || 0
  if (!rows || !cols) return grid

  const colorMap = new Map(brand.colors.map((color) => {
    const rgb = hexToRgb(color.hex)
    const max = Math.max(rgb.r, rgb.g, rgb.b)
    const min = Math.min(rgb.r, rgb.g, rgb.b)
    return [color.id, {
      ...rgb,
      luma: 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b,
      chroma: max - min,
    }]
  }))

  const borderCount = new Map()
  const countBorder = (x, y) => {
    const id = grid[y][x]
    if (!id) return
    borderCount.set(id, (borderCount.get(id) || 0) + 1)
  }
  for (let x = 0; x < cols; x++) {
    countBorder(x, 0)
    if (rows > 1) countBorder(x, rows - 1)
  }
  for (let y = 1; y < rows - 1; y++) {
    countBorder(0, y)
    if (cols > 1) countBorder(cols - 1, y)
  }

  const removableBackgroundIds = new Set(
    brand.colors
      .filter((color) => color.id === 'mard_H02' || (brand.id?.startsWith('mard') && color.name === 'H02'))
      .map((color) => color.id)
  )
  if (removableBackgroundIds.size === 0) return grid

  let backgroundId = null
  let backgroundCount = 0
  for (const [id, count] of borderCount) {
    if (!removableBackgroundIds.has(id)) continue
    const color = colorMap.get(id)
    if (!color) continue
    if (count > backgroundCount) {
      backgroundId = id
      backgroundCount = count
    }
  }

  const borderLength = rows === 1
    ? cols
    : cols === 1
      ? rows
      : 2 * cols + 2 * rows - 4
  if (!backgroundId || backgroundCount < borderLength * 0.35) return grid

  const backgroundIds = new Set([backgroundId])
  const sourceMatchesBackground = (x, y) => {
    if (!sourceGrid) return true
    const sourceColor = sourceGrid[y]?.[x]
    if (!sourceColor) return true
    return findNearestColor(sourceColor.r, sourceColor.g, sourceColor.b, brand.colors)?.id === backgroundId
  }

  const isBackground = Array.from({ length: rows }, () => Array(cols).fill(false))
  const stack = []
  const push = (x, y) => {
    if (x < 0 || x >= cols || y < 0 || y >= rows || isBackground[y][x]) return
    if (!backgroundIds.has(grid[y][x])) return
    if (!sourceMatchesBackground(x, y)) return
    isBackground[y][x] = true
    stack.push([x, y])
  }

  for (let x = 0; x < cols; x++) {
    push(x, 0)
    push(x, rows - 1)
  }
  for (let y = 1; y < rows - 1; y++) {
    push(0, y)
    if (cols > 1) push(cols - 1, y)
  }

  let backgroundArea = 0
  while (stack.length) {
    const [x, y] = stack.pop()
    backgroundArea += 1
    push(x + 1, y)
    push(x - 1, y)
    push(x, y + 1)
    push(x, y - 1)
  }

  if (backgroundArea < rows * cols * 0.08) return grid

  const detailMask = sourceGrid ? buildDetailMask(sourceGrid) : null
  const preserveSubjectWhite = Array.from({ length: rows }, () => Array(cols).fill(false))
  const isForegroundId = (id) => id && !backgroundIds.has(id)
  const isDetailedWhiteCell = (x, y) => detailMask ? detailMask[y]?.[x] >= 0.3 : false
  const foregroundSupport = (x, y, radius) => {
    let total = 0
    let foreground = 0
    let closeForeground = 0
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx === 0 && dy === 0) continue
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
        total += 1
        if (!isForegroundId(grid[ny][nx])) continue
        foreground += 1
        if (Math.abs(dx) + Math.abs(dy) <= 2) closeForeground += 1
      }
    }
    return { total, foreground, closeForeground }
  }

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (!isBackground[y][x]) continue
      const support = foregroundSupport(x, y, 2)
      const foregroundRatio = support.total ? support.foreground / support.total : 0
      const surroundedBySubject = support.closeForeground >= 3 || foregroundRatio >= 0.28
      const detailedNearSubject = support.closeForeground >= 1 && isDetailedWhiteCell(x, y)
      if (surroundedBySubject || detailedNearSubject) preserveSubjectWhite[y][x] = true
    }
  }

  // 扩一圈保护，避免白色衣物边缘因为量化成背景色而被掏空。
  for (let pass = 0; pass < 2; pass++) {
    const next = preserveSubjectWhite.map((row) => [...row])
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (!isBackground[y][x] || preserveSubjectWhite[y][x]) continue
        let protectedNeighbors = 0
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = x + dx
          const ny = y + dy
          if (nx >= 0 && nx < cols && ny >= 0 && ny < rows && preserveSubjectWhite[ny][nx]) protectedNeighbors += 1
        }
        if (protectedNeighbors >= 2 || (protectedNeighbors >= 1 && isDetailedWhiteCell(x, y))) next[y][x] = true
      }
    }
    for (let y = 0; y < rows; y++) preserveSubjectWhite[y] = next[y]
  }

  return grid.map((row, y) => row.map((id, x) => (isBackground[y][x] && !preserveSubjectWhite[y][x] ? null : id)))
}

function enhancePixelGridForBeads(pixelGrid, strength = 0) {
  const amount = clamp01(strength)
  if (!amount) return pixelGrid

  const rows = pixelGrid.length
  const cols = pixelGrid[0]?.length || 0
  if (!rows || !cols) return pixelGrid

  const detailMask = buildDetailMask(pixelGrid)
  const sigmaColor = 34 + amount * 18
  const sigmaSquared = 2 * sigmaColor * sigmaColor

  return pixelGrid.map((row, y) => row.map((color, x) => {
    let ar = 0
    let ag = 0
    let ab = 0
    let weightSum = 0

    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const ny = y + dy
        const nx = x + dx
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
        const neighbor = pixelGrid[ny][nx]
        const spatialWeight = dx === 0 && dy === 0 ? 1.8 : dx === 0 || dy === 0 ? 1 : 0.68
        const distance = rgbDistance(color, neighbor)
        const rangeWeight = Math.exp(-(distance * distance) / sigmaSquared)
        const weight = spatialWeight * rangeWeight
        ar += neighbor.r * weight
        ag += neighbor.g * weight
        ab += neighbor.b * weight
        weightSum += weight
      }
    }

    const localAverage = { r: ar / weightSum, g: ag / weightSum, b: ab / weightSum }
    const detail = detailMask[y][x]
    const smoothAmount = amount * 0.5 * (1 - detail) ** 2
    const sharpenAmount = amount * 0.16 * detail

    return {
      r: clampByte(color.r + (localAverage.r - color.r) * smoothAmount + (color.r - localAverage.r) * sharpenAmount),
      g: clampByte(color.g + (localAverage.g - color.g) * smoothAmount + (color.g - localAverage.g) * sharpenAmount),
      b: clampByte(color.b + (localAverage.b - color.b) * smoothAmount + (color.b - localAverage.b) * sharpenAmount),
    }
  }))
}

function boostPixelGridVibrance(pixelGrid, saturation) {
  if (!saturation || saturation === 1) return pixelGrid
  return pixelGrid.map((row) => row.map((color) => boostVibrance(color, saturation)))
}

function buildPatternFromPixelGrid(
  pixelGrid,
  { brand, maxColors, dithering, mergeThreshold, denoise = 0, faithful = false, saturation = 1, preprocessStrength = 0, outline = false, removeBackground = false }
) {
  const rows = pixelGrid.length
  const cols = pixelGrid[0]?.length || 0
  if (!rows || !cols) {
    return { grid: [], legend: [], width: 0, height: 0, total: 0, brandId: brand.id }
  }

  let workingGrid = pixelGrid
  if (!faithful && preprocessStrength > 0) {
    workingGrid = enhancePixelGridForBeads(workingGrid, preprocessStrength)
  }
  if (!faithful && saturation && saturation !== 1) {
    workingGrid = boostPixelGridVibrance(workingGrid, saturation)
  }

  let palette = maxColors > 0 ? reducePalette(workingGrid, brand.colors, maxColors) : brand.colors
  if (!faithful && removeBackground) {
    const edgeBackgroundColor = brand.colors.find((color) =>
      color.id === 'mard_H02' || (brand.id?.startsWith('mard') && color.name === 'H02')
    )
    if (edgeBackgroundColor && !palette.some((color) => color.id === edgeBackgroundColor.id)) {
      palette = [...palette, edgeBackgroundColor]
    }
  }
  let grid = dithering && !faithful
    ? floydSteinbergDither(workingGrid, palette, 0.48 + preprocessStrength * 0.12)
    : workingGrid.map((row) => row.map((c) => findNearestColor(c.r, c.g, c.b, palette).id))

  if (!faithful && removeBackground) {
    grid = removeLightEdgeBackground(grid, brand, workingGrid)
  }

  if (!faithful && mergeThreshold > 0) {
    grid = mergeSimilarColors(grid, palette, mergeThreshold)
  }

  if (!faithful && denoise > 0) {
    grid = denoiseGrid(grid, denoise, workingGrid, palette)
  }

  if (!faithful && outline) {
    applyOutline(grid, brand)
  }

  const legend = new Map()
  let total = 0
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const id = grid[y][x]
      const color = brand.colors.find((c) => c.id === id)
      if (!color) continue
      const entry = legend.get(id) || { ...color, count: 0 }
      entry.count += 1
      legend.set(id, entry)
      total += 1
    }
  }

  return {
    grid,
    legend: Array.from(legend.values()),
    width: cols,
    height: rows,
    total,
    brandId: brand.id,
  }
}

/**
 * 从已调整好的 sourceCanvas（尺寸 = 目标 bead 网格尺寸）生成拼豆 pattern
 * @param {HTMLCanvasElement} sourceCanvas 已缩放/裁剪好的源图 canvas，width/height 即为网格尺寸
 * @param {Object} options
 * @param {Object} options.brand 品牌对象，含 colors 数组
 * @param {number} options.maxColors 最大颜色数，0 表示不限
 * @param {boolean} options.dithering 是否启用 Floyd-Steinberg 抖动
 * @param {number} options.mergeThreshold 相似色合并阈值，0 表示不合并
 * @param {'average'|'dominant'} options.pixelMode 取色方式
 * @param {boolean} options.outline 是否自动描边（预览管线也走，所见即所得）
 * @returns {{grid: string[][], legend: Array, width: number, height: number, total: number}}
 */
export function generateBeadPattern(
  sourceCanvas,
  { brand, maxColors, dithering, mergeThreshold, pixelMode = 'average', sampleScale = 1, denoise = 0, faithful = false, saturation = 1, preprocessStrength = 0, outline = false, removeBackground = false }
) {
  const fullW = sourceCanvas.width
  const fullH = sourceCanvas.height
  const cols = Math.max(1, Math.round(fullW / sampleScale))
  const rows = Math.max(1, Math.round(fullH / sampleScale))
  const ctx = sourceCanvas.getContext('2d')
  const imageData = ctx.getImageData(0, 0, fullW, fullH)

  // 高还原模式（AI 像素图）：主色采样，不做抖动/合并/去杂色，最大化保留原图
  const effPixelMode = faithful ? 'dominant' : pixelMode

  const pixelGrid = []
  for (let y = 0; y < rows; y++) {
    const row = []
    for (let x = 0; x < cols; x++) {
      const color = getCellColor(imageData, x * sampleScale, y * sampleScale, sampleScale, sampleScale, effPixelMode)
      row.push(color || { r: 255, g: 255, b: 255 })
    }
    pixelGrid.push(row)
  }

  return buildPatternFromPixelGrid(pixelGrid, {
    brand,
    maxColors,
    dithering,
    mergeThreshold,
    denoise,
    faithful,
    saturation,
    preprocessStrength,
    outline,
    removeBackground,
  })
}

/**
 * 直接从原图按“每 bead 对应原图区域”采样，避免先缩放到 gridSize 再读 1×1 导致的细节丢失。
 * 这是 pingdoudou 等像素化工具的核心思路：每个格子从原始高分辨率区域取 average/dominant。
 * @param {HTMLImageElement} img
 * @param {{x:number,y:number,scale:number}} transform 编辑器中的平移/缩放
 * @param {number} gridSize
 * @param {number} editorSize
 * @param {Object} options
 */
export function generateBeadPatternFromEditorImage(
  img,
  transform,
  gridSize,
  editorSize,
  { brand, maxColors, dithering, mergeThreshold, pixelMode = 'average', outline = true, gridAnchor = null, denoise = 0, faithful = false, saturation = 1, preprocessStrength = 0, removeBackground = false }
) {
  if (!img) return null
  // 高还原模式（AI 像素图）：主色采样、不描边、不抖动/合并/去杂色
  const effPixelMode = faithful ? 'dominant' : pixelMode
  const naturalW = img.naturalWidth || img.width
  const naturalH = img.naturalHeight || img.height
  if (!naturalW || !naturalH) return null

  // 1. 把原图画到一张分辨率受控的 canvas 上，避免超大图撑爆内存
  const srcScale = Math.min(1, MAX_SOURCE_SIZE / Math.max(naturalW, naturalH))
  const srcW = Math.max(1, Math.round(naturalW * srcScale))
  const srcH = Math.max(1, Math.round(naturalH * srcScale))
  const srcCanvas = document.createElement('canvas')
  srcCanvas.width = srcW
  srcCanvas.height = srcH
  const srcCtx = srcCanvas.getContext('2d')
  srcCtx.imageSmoothingEnabled = !faithful
  srcCtx.imageSmoothingQuality = 'high'
  srcCtx.drawImage(img, 0, 0, srcW, srcH)
  const srcImageData = srcCtx.getImageData(0, 0, srcW, srcH)

  // 2. 图片在编辑器里的几何位置
  const imgW = naturalW * transform.scale
  const imgH = naturalH * transform.scale
  const imgX = editorSize / 2 + transform.x - imgW / 2
  const imgY = editorSize / 2 + transform.y - imgH / 2
  // 锁定锚点模式：以锁定的内容点为基准对齐网格，格子尺寸随缩放变化，
  // 板子规格（格子数）由当前缩放反推
  const outSize = gridAnchor ? gridAnchor.gridSize : gridSize
  const cellSize = gridAnchor ? gridAnchor.cellSize : editorSize / gridSize
  const offX = gridAnchor ? gridAnchor.offsetX : 0
  const offY = gridAnchor ? gridAnchor.offsetY : 0

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

  // 3. 对每个 bead 格，取它在原图上对应的区域做颜色统计
  const pixelGrid = []
  for (let j = 0; j < outSize; j++) {
    const row = []
    for (let i = 0; i < outSize; i++) {
      const destX = offX + i * cellSize
      const destY = offY + j * cellSize
      const destX2 = gridAnchor ? destX + cellSize : Math.min(editorSize, destX + cellSize)
      const destY2 = gridAnchor ? destY + cellSize : Math.min(editorSize, destY + cellSize)

      // 与图片可见区域求交
      const ix = Math.max(imgX, destX)
      const iy = Math.max(imgY, destY)
      const ix2 = Math.min(imgX + imgW, destX2)
      const iy2 = Math.min(imgY + imgH, destY2)
      const iw = Math.max(0, ix2 - ix)
      const ih = Math.max(0, iy2 - iy)

      let color = null
      if (iw > 0 && ih > 0) {
        const sx0 = clamp(Math.floor((ix - imgX) / transform.scale * srcScale), 0, srcW - 1)
        const sy0 = clamp(Math.floor((iy - imgY) / transform.scale * srcScale), 0, srcH - 1)
        const sx1 = clamp(Math.ceil((ix2 - imgX) / transform.scale * srcScale), 0, srcW)
        const sy1 = clamp(Math.ceil((iy2 - imgY) / transform.scale * srcScale), 0, srcH)
        const sw = Math.max(1, sx1 - sx0)
        const sh = Math.max(1, sy1 - sy0)

        color = getCellColor(srcImageData, sx0, sy0, sw, sh, effPixelMode)
      }
      row.push(color || { r: 255, g: 255, b: 255 })
    }
    pixelGrid.push(row)
  }

  return buildPatternFromPixelGrid(pixelGrid, {
    brand,
    maxColors,
    dithering,
    mergeThreshold,
    denoise,
    faithful,
    saturation,
    preprocessStrength,
    outline,
    removeBackground,
  })
}

/**
 * 把已有 pattern 映射到新的品牌色库
 * 用于切换品牌时不丢失已有图纸
 */
export function remapPatternBrand(pattern, newBrand) {
  if (!pattern || pattern.brandId === newBrand.id) return pattern
  const oldColorMap = new Map(pattern.legend.map((c) => [c.id, c]))
  const idMap = new Map()
  for (const [oldId, oldColor] of oldColorMap) {
    const rgb = hexToRgb(oldColor.hex)
    const nearest = findNearestColor(rgb.r, rgb.g, rgb.b, newBrand.colors)
    idMap.set(oldId, nearest ? nearest.id : oldId)
  }

  const newGrid = pattern.grid.map((row) => row.map((id) => idMap.get(id) || id))
  const legend = new Map()
  let total = 0
  for (let y = 0; y < pattern.height; y++) {
    for (let x = 0; x < pattern.width; x++) {
      const id = newGrid[y][x]
      const color = newBrand.colors.find((c) => c.id === id)
      if (!color) continue
      const entry = legend.get(id) || { ...color, count: 0 }
      entry.count += 1
      legend.set(id, entry)
      total += 1
    }
  }

  return {
    grid: newGrid,
    legend: Array.from(legend.values()),
    width: pattern.width,
    height: pattern.height,
    total,
    brandId: newBrand.id,
  }
}

/**
 * 把 pattern 渲染成 PNG DataURL，用于图集上传
 */
export function renderPatternToDataURL(pattern, brand, scale = 16) {
  const { grid, width, height } = pattern
  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale
  const ctx = canvas.getContext('2d')
  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const hex = colorMap.get(grid[y][x])
      if (!hex) continue
      ctx.fillStyle = hex
      ctx.fillRect(x * scale, y * scale, scale, scale)
    }
  }

  return canvas.toDataURL('image/png')
}
